<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import Tag from 'primevue/tag';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import TokenActionConfirmationDialog from '@/components/common/TokenActionConfirmationDialog.vue';
import type { PersonBasicData, PersonPhone, PersonAddress, PersonEmail } from '@/types/person';
import type { ExtendedDataProductKey } from '@/api/modules/personApi';
import { formatDateTime } from '@/utils/formatters';
import { DATA_UNIT_PRODUCT_KEYS } from '@/constants/dataUnitProducts';
import { useDataUnitPrices } from '@/composables/useDataUnitPrices';

const props = defineProps<{
  personData: PersonBasicData | null;
  hasAddressData: boolean | null;
  hasPhonesData: boolean | null;
  hasEmailsData: boolean | null;
  isRequestingAddresses: boolean;
  isRequestingPhones: boolean;
  isRequestingEmails: boolean;
  requestError: string | null;
}>();

const emit = defineEmits<{
  (e: 'requestExtendedData', datasets: ExtendedDataProductKey[]): void;
}>();

// Expandable states for inactive items
const showInactiveAddresses = ref(false);
const showInactivePhones = ref(false);
const showInactiveEmails = ref(false);

// Confirmation modal state
const showConfirmDialog = ref(false);
const pendingDatasetRequest = ref<ExtendedDataProductKey | null>(null);

const datasetLabels: Record<ExtendedDataProductKey, string> = {
  [DATA_UNIT_PRODUCT_KEYS.personEmails]: 'Emails',
  [DATA_UNIT_PRODUCT_KEYS.personPhones]: 'Telefones',
  [DATA_UNIT_PRODUCT_KEYS.personAddresses]: 'Endereços'
};

const {
  loading: dataUnitPriceLoading,
  error: dataUnitPriceError,
  loadDataUnitPrices,
  getDataUnitPriceLabel,
} = useDataUnitPrices();

const pendingProductKey = computed(() =>
  pendingDatasetRequest.value
);
const pendingPriceLabel = computed(() => getDataUnitPriceLabel(pendingProductKey.value));

function openConfirmDialog(dataset: ExtendedDataProductKey) {
  pendingDatasetRequest.value = dataset;
  void loadDataUnitPrices([dataset]);
  showConfirmDialog.value = true;
}

function confirmRequest() {
  if (pendingDatasetRequest.value) {
    emit('requestExtendedData', [pendingDatasetRequest.value]);
  }
  showConfirmDialog.value = false;
  pendingDatasetRequest.value = null;
}

function cancelRequest() {
  showConfirmDialog.value = false;
  pendingDatasetRequest.value = null;
}

watch(showConfirmDialog, isVisible => {
  if (!isVisible) {
    pendingDatasetRequest.value = null;
  }
});

// Format timestamp for display
const formatTimestamp = (timestamp: string | null): string => {
  if (!timestamp) return '';
  return formatDateTime(timestamp, timestamp);
};

// Active-only computed
const activeAddresses = computed((): PersonAddress[] => {
  if (!props.personData?.addresses) return [];
  return props.personData.addresses.filter(a => a.isActive === true);
});

const activePhones = computed((): PersonPhone[] => {
  if (!props.personData?.phones) return [];
  return props.personData.phones.filter(p => p.isActive === true);
});

const activeEmails = computed((): PersonEmail[] => {
  if (!props.personData?.emails) return [];
  return props.personData.emails.filter(e => e.isActive === true);
});

// Inactive-only computed
const inactiveAddresses = computed((): PersonAddress[] => {
  if (!props.personData?.addresses) return [];
  return props.personData.addresses.filter(a => a.isActive !== true);
});

const inactivePhones = computed((): PersonPhone[] => {
  if (!props.personData?.phones) return [];
  return props.personData.phones.filter(p => p.isActive !== true);
});

const inactiveEmails = computed((): PersonEmail[] => {
  if (!props.personData?.emails) return [];
  return props.personData.emails.filter(e => e.isActive !== true);
});

// Counts
const inactiveAddressCount = computed(() => inactiveAddresses.value.length);
const inactivePhoneCount = computed(() => inactivePhones.value.length);
const inactiveEmailCount = computed(() => inactiveEmails.value.length);
</script>

<template>
  <div class="grid grid-cols-1 gap-4">
    <div class="info-card contact-card-compact">
      <div class="info-card-header">
        <span class="material-icons">contacts</span>
        <h3>Contato</h3>
      </div>

      <div class="contact-list-compact">
        <!-- EMAILS -->
        <div class="contact-row">
          <div class="contact-row-icon">
            <span class="material-icons">email</span>
          </div>
          <div class="contact-row-content">
            <div class="contact-row-label">
              <span>EMAIL</span>
              <span v-if="personData?.emailsInsertedAt" class="contact-timestamp">{{ formatTimestamp(personData.emailsInsertedAt) }}</span>
              <Button
                v-if="!hasEmailsData && !isRequestingEmails"
                label="Solicitar"
                @click="openConfirmDialog(DATA_UNIT_PRODUCT_KEYS.personEmails)"
                size="small"
                text
                class="request-data-btn-inline"
              >
                <template #icon>
                  <BaseIcon>download</BaseIcon>
                </template>
              </Button>
            </div>

            <!-- Loading -->
            <div v-if="isRequestingEmails" class="contact-row-loading">
              <ProgressSpinner style="width: 16px; height: 16px" strokeWidth="4" />
              <span>Carregando...</span>
            </div>

            <!-- No data -->
            <div v-else-if="!hasEmailsData" class="contact-row-empty">
              Dados não solicitados
            </div>

            <!-- Has data with active items -->
            <div v-else-if="activeEmails.length > 0" class="contact-row-values">
              <div v-for="(email, index) in activeEmails" :key="'email-' + index" class="contact-value-item">
                <span class="contact-value">{{ email.address }}</span>
                <span v-if="email.isMain" class="contact-main-tag">Principal</span>
                <span v-if="email.type" class="contact-type">{{ email.type === 'PERSONAL' ? 'Pessoal' : email.type === 'CORPORATE' ? 'Corporativo' : email.type }}</span>
              </div>
            </div>

            <!-- No active items -->
            <div v-else class="contact-row-empty">
              Nenhum email ativo
            </div>

            <!-- Inactive items toggle -->
            <button
              v-if="inactiveEmailCount > 0"
              class="contact-inactive-toggle"
              @click="showInactiveEmails = !showInactiveEmails"
            >
              <span class="material-icons">{{ showInactiveEmails ? 'expand_less' : 'expand_more' }}</span>
              {{ inactiveEmailCount }} {{ inactiveEmailCount === 1 ? 'inativo' : 'inativos' }}
            </button>

            <!-- Inactive items list -->
            <div v-if="showInactiveEmails && inactiveEmails.length > 0" class="contact-inactive-list">
              <div v-for="(email, index) in inactiveEmails" :key="'inactive-email-' + index" class="contact-value-item contact-value-inactive">
                <span class="contact-value">{{ email.address }}</span>
                <span v-if="email.isMain" class="contact-main-tag">Principal</span>
                <span v-if="email.type" class="contact-type">{{ email.type === 'PERSONAL' ? 'Pessoal' : email.type === 'CORPORATE' ? 'Corporativo' : email.type }}</span>
                <Tag value="Inativo" severity="danger" class="!text-[9px] !py-0 !px-1" />
              </div>
            </div>
          </div>
        </div>

        <!-- PHONES -->
        <div class="contact-row">
          <div class="contact-row-icon">
            <span class="material-icons">phone</span>
          </div>
          <div class="contact-row-content">
            <div class="contact-row-label">
              <span>TELEFONE</span>
              <span v-if="personData?.phonesInsertedAt" class="contact-timestamp">{{ formatTimestamp(personData.phonesInsertedAt) }}</span>
              <Button
                v-if="!hasPhonesData && !isRequestingPhones"
                label="Solicitar"
                @click="openConfirmDialog(DATA_UNIT_PRODUCT_KEYS.personPhones)"
                size="small"
                text
                class="request-data-btn-inline"
              >
                <template #icon>
                  <BaseIcon>download</BaseIcon>
                </template>
              </Button>
            </div>

            <!-- Loading -->
            <div v-if="isRequestingPhones" class="contact-row-loading">
              <ProgressSpinner style="width: 16px; height: 16px" strokeWidth="4" />
              <span>Carregando...</span>
            </div>

            <!-- No data -->
            <div v-else-if="!hasPhonesData" class="contact-row-empty">
              Dados não solicitados
            </div>

            <!-- Has data with active items -->
            <div v-else-if="activePhones.length > 0" class="contact-row-values">
              <div v-for="(phone, index) in activePhones" :key="'phone-' + index" class="contact-value-item">
                <span class="contact-value">{{ phone.number }}</span>
                <span v-if="phone.isMain" class="contact-main-tag">Principal</span>
                <span v-if="phone.carrier" class="contact-type">{{ phone.carrier }}</span>
              </div>
            </div>

            <!-- No active items -->
            <div v-else class="contact-row-empty">
              Nenhum telefone ativo
            </div>

            <!-- Inactive items toggle -->
            <button
              v-if="inactivePhoneCount > 0"
              class="contact-inactive-toggle"
              @click="showInactivePhones = !showInactivePhones"
            >
              <span class="material-icons">{{ showInactivePhones ? 'expand_less' : 'expand_more' }}</span>
              {{ inactivePhoneCount }} {{ inactivePhoneCount === 1 ? 'inativo' : 'inativos' }}
            </button>

            <!-- Inactive items list -->
            <div v-if="showInactivePhones && inactivePhones.length > 0" class="contact-inactive-list">
              <div v-for="(phone, index) in inactivePhones" :key="'inactive-phone-' + index" class="contact-value-item contact-value-inactive">
                <span class="contact-value">{{ phone.number }}</span>
                <span v-if="phone.isMain" class="contact-main-tag">Principal</span>
                <span v-if="phone.carrier" class="contact-type">{{ phone.carrier }}</span>
                <Tag value="Inativo" severity="danger" class="!text-[9px] !py-0 !px-1" />
              </div>
            </div>
          </div>
        </div>

        <!-- ADDRESSES -->
        <div class="contact-row">
          <div class="contact-row-icon">
            <span class="material-icons">location_on</span>
          </div>
          <div class="contact-row-content">
            <div class="contact-row-label">
              <span>ENDEREÇO</span>
              <span v-if="personData?.addressesInsertedAt" class="contact-timestamp">{{ formatTimestamp(personData.addressesInsertedAt) }}</span>
              <Button
                v-if="!hasAddressData && !isRequestingAddresses"
                label="Solicitar"
                @click="openConfirmDialog(DATA_UNIT_PRODUCT_KEYS.personAddresses)"
                size="small"
                text
                class="request-data-btn-inline"
              >
                <template #icon>
                  <BaseIcon>download</BaseIcon>
                </template>
              </Button>
            </div>

            <!-- Loading -->
            <div v-if="isRequestingAddresses" class="contact-row-loading">
              <ProgressSpinner style="width: 16px; height: 16px" strokeWidth="4" />
              <span>Carregando...</span>
            </div>

            <!-- No data -->
            <div v-else-if="!hasAddressData" class="contact-row-empty">
              Dados não solicitados
            </div>

            <!-- Has data with active items -->
            <div v-else-if="activeAddresses.length > 0" class="contact-row-values">
              <div v-for="(address, index) in activeAddresses" :key="'address-' + index" class="contact-value-item contact-address-item">
                <div class="contact-address-main">
                  <span class="contact-value">{{ address.street }}, {{ address.number }}</span>
                  <span v-if="address.isMain" class="contact-main-tag">Principal</span>
                </div>
                <div v-if="address.complement" class="contact-address-line">{{ address.complement }}</div>
                <div class="contact-address-line">{{ address.neighborhood }} - {{ address.city }}/{{ address.state }} - {{ address.cep }}</div>
              </div>
            </div>

            <!-- No active items -->
            <div v-else class="contact-row-empty">
              Nenhum endereço ativo
            </div>

            <!-- Inactive items toggle -->
            <button
              v-if="inactiveAddressCount > 0"
              class="contact-inactive-toggle"
              @click="showInactiveAddresses = !showInactiveAddresses"
            >
              <span class="material-icons">{{ showInactiveAddresses ? 'expand_less' : 'expand_more' }}</span>
              {{ inactiveAddressCount }} {{ inactiveAddressCount === 1 ? 'inativo' : 'inativos' }}
            </button>

            <!-- Inactive items list -->
            <div v-if="showInactiveAddresses && inactiveAddresses.length > 0" class="contact-inactive-list">
              <div v-for="(address, index) in inactiveAddresses" :key="'inactive-address-' + index" class="contact-value-item contact-address-item contact-value-inactive">
                <div class="contact-address-main">
                  <span class="contact-value">{{ address.street }}, {{ address.number }}</span>
                  <span v-if="address.isMain" class="contact-main-tag">Principal</span>
                  <Tag value="Inativo" severity="danger" class="!text-[9px] !py-0 !px-1" />
                </div>
                <div v-if="address.complement" class="contact-address-line">{{ address.complement }}</div>
                <div class="contact-address-line">{{ address.neighborhood }} - {{ address.city }}/{{ address.state }} - {{ address.cep }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p v-if="requestError" class="contact-request-error">{{ requestError }}</p>
    </div>
  </div>

  <TokenActionConfirmationDialog
    v-model:visible="showConfirmDialog"
    title="Confirmar Solicitação"
    message-prefix="Você está prestes a solicitar dados de "
    :highlighted-text="pendingDatasetRequest ? datasetLabels[pendingDatasetRequest] : null"
    :cost-label="pendingPriceLabel"
    :cost-error="dataUnitPriceError"
    :cost-loading="dataUnitPriceLoading"
    @confirm="confirmRequest"
    @cancel="cancelRequest"
  />
</template>

<style scoped>
.info-card {
  background: #ffffff;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid #e9ecef;
}

.info-card-header {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 1.1rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #f1f3f5;
}

.info-card-header .material-icons {
  font-size: 1.5rem;
  color: var(--primary-color, #007bff);
}

/* Unified Contact Section - Compact Vertical Layout */
.contact-card-compact {
  background: #ffffff;
}

.contact-list-compact {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.contact-row {
  display: flex;
  gap: 1rem;
  padding: 1rem 0;
  border-bottom: 1px solid #f1f3f5;
}

.contact-row:last-child {
  border-bottom: none;
  padding-bottom: 0;
}

.contact-row:first-child {
  padding-top: 0;
}

.contact-row-icon {
  flex-shrink: 0;
  width: 24px;
  display: flex;
  align-items: flex-start;
  padding-top: 2px;
}

.contact-row-icon .material-icons {
  font-size: 1.25rem;
  color: #868e96;
}

.contact-row-content {
  flex: 1;
  min-width: 0;
}

.contact-row-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  color: #868e96;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 0.25rem;
}

.contact-timestamp {
  font-weight: 400;
  font-size: 0.65rem;
  color: #adb5bd;
  text-transform: none;
  letter-spacing: normal;
}

.contact-row-loading {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #868e96;
  padding: 0.25rem 0;
}

.contact-request-error {
  margin: 0.75rem 0 0;
  color: #b42318;
  font-size: 0.85rem;
}

.contact-row-empty {
  font-size: 0.85rem;
  color: #adb5bd;
  font-style: italic;
}

.request-data-btn-inline {
  margin-left: auto;
  padding: 0.15rem 0.5rem !important;
  font-size: 0.65rem !important;
  height: auto !important;
}

.request-data-btn-inline :deep(.p-button-icon) {
  font-size: 0.7rem !important;
}

.request-data-btn-inline :deep(.p-button-label) {
  font-weight: 500;
}

.contact-row-values {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.contact-value-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.contact-value {
  font-size: 0.95rem;
  font-weight: 500;
  color: #212529;
}

.contact-main-tag {
  font-size: 0.6rem;
  font-weight: 600;
  color: #b45309;
  background: #fef3c7;
  padding: 0.1rem 0.35rem;
  border-radius: 3px;
  text-transform: uppercase;
}

.contact-type {
  font-size: 0.75rem;
  color: #868e96;
}

.contact-address-item {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.125rem;
}

.contact-address-main {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.contact-address-line {
  font-size: 0.8rem;
  color: #6c757d;
}

.contact-value-inactive {
  opacity: 0.7;
}

.contact-value-inactive .contact-value {
  color: #6c757d;
}

.contact-inactive-toggle {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  background: none;
  border: none;
  font-size: 0.7rem;
  color: #868e96;
  cursor: pointer;
  padding: 0.25rem 0;
  margin-top: 0.5rem;
  transition: color 0.15s ease;
}

.contact-inactive-toggle:hover {
  color: #495057;
}

.contact-inactive-toggle .material-icons {
  font-size: 1rem;
}

.contact-inactive-list {
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
  margin-top: 0.375rem;
  padding-left: 0.5rem;
  border-left: 2px solid #e9ecef;
}
</style>
